import { useEffect } from "react";

interface InjectorProps {
  trackingJsCode?: string;
}

// Injects conversion tracking HTML/JS into the page.
// Script tags are recreated so browsers execute them (innerHTML-cloned scripts don't run).
export const TrackingCodeInjector = ({ trackingJsCode }: InjectorProps) => {
  useEffect(() => {
    if (!trackingJsCode) return;

    let isCancelled = false;
    const injectedScripts: HTMLScriptElement[] = [];
    const injectedNodes: ChildNode[] = [];

    // Use a detached element for parsing — never a React-managed node,
    // so there is no reconciler conflict on unmount.
    const tempParser = document.createElement("div");
    tempParser.innerHTML = trackingJsCode;

    const originalScripts = Array.from(tempParser.querySelectorAll("script"));
    originalScripts.forEach(s => s.remove());

    // Inject remaining non-script nodes (img pixels, noscript, etc.) so
    // pixel-based tracking fires as network requests.
    Array.from(tempParser.childNodes).forEach(node => {
      const cloned = node.cloneNode(true) as ChildNode;
      document.body.appendChild(cloned);
      injectedNodes.push(cloned);
    });

    const processScripts = async () => {
      for (const scriptTag of originalScripts) {
        if (isCancelled) break;

        const newScript = document.createElement("script");

        Array.from(scriptTag.attributes).forEach(attr => {
          newScript.setAttribute(attr.name, attr.value);
        });

        if (!scriptTag.src && scriptTag.innerHTML) {
          newScript.textContent = scriptTag.innerHTML;
        }

        injectedScripts.push(newScript);
        document.body.appendChild(newScript);

        if (scriptTag.src) {
          await new Promise<void>(resolve => {
            const handleLoad = () => {
              resolve();
              newScript.removeEventListener("load", handleLoad);
              newScript.removeEventListener("error", handleLoad);
            };
            newScript.addEventListener("load", handleLoad);
            newScript.addEventListener("error", handleLoad);
          });
        }
      }
    };

    // Defer script injection by one macrotask so React's concurrent-mode
    // commit phase finishes cleaning up the previous route's components
    // before GTM/Facebook Pixel scripts touch the DOM. Without this,
    // inline scripts run synchronously during appendChild and call
    // insertBefore on nodes React is still tearing down, triggering a
    // NotFoundError: Failed to execute 'removeChild' on 'Node'.
    const timeoutId = setTimeout(() => void processScripts(), 0);

    return () => {
      clearTimeout(timeoutId);
      isCancelled = true;
      injectedScripts.forEach(script => script.parentNode?.removeChild(script));
      injectedNodes.forEach(node => node.parentNode?.removeChild(node));
    };
  }, [trackingJsCode]);

  return null;
};
